/* eslint-disable camelcase -- GET query params use snake_case (gateway / API) */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useNavigate } from 'react-router-dom';
import { Box } from 'grommet';
import { useAppTheme } from '@Components/Context';
import { Seo, InfoNotice } from '@Components/Control';
import { AdminManualClaimsRequestsTablePartial } from '@Components/Partial/Product/ContentId';
import { AdminNavWrapper } from '@Components/Navigation';
import { paths } from '@Components/configs';
import {
  initiateAdminFetchManualClaimRequests,
  fetchCompanyListRequest,
} from '@Actions';

const PAGE_SIZE = 25;


function useDebouncedValue(value, delayMs) {
  const [debounced, setDebounced] = React.useState(value);
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

const AdminManualClaimsListPage = ({
  small,
  location,
  fetchList,
  fetchCompanyList,
  companyList,
  loading,
}) => {
  const navigate = useNavigate();
  const { theme: { page }, isDarkMode } = useAppTheme();
  const usePageBg = isDarkMode ? page.background : page.altBg;

  const [listData, setListData] = React.useState(null);
  const [currentPage, setCurrentPage] = React.useState(0);
  const [query, setQuery] = React.useState({});
  const [companyIdInput, setCompanyIdInput] = React.useState('');
  const debouncedCompanyId = useDebouncedValue(companyIdInput, 450);
  const companyOptions = (companyList || []).map(({ id, name }) => ({
    api: id,
    display: name,
  }));
  const companyNameById = React.useMemo(
    () => (companyList || []).reduce((acc, { id, name }) => {
      acc[String(id)] = name;
      return acc;
    }, {}),
    [companyList],
  );

  React.useEffect(() => {
    fetchCompanyList();
  }, [fetchCompanyList]);

  const queryParams = React.useMemo(() => {
    const p = {
      limit: PAGE_SIZE,
      offset: currentPage * PAGE_SIZE,
      sort_order: query.sortOrder || 'desc',
    };
    if (query.status) p.status = query.status;
    if (query.createdAfter) p.created_after = query.createdAfter;
    if (query.createdBefore) p.created_before = query.createdBefore;
    const cid = String(debouncedCompanyId || '').trim();
    if (cid && /^\d+$/.test(cid)) {
      p.company_id = Number(cid);
    }
    if (query.search) p.search = query.search;
    return p;
  }, [
    currentPage,
    query.status,
    query.createdAfter,
    query.createdBefore,
    query.sortOrder,
    query.search,
    debouncedCompanyId,
  ]);

  React.useEffect(() => {
    setCurrentPage(0);
  }, [
    query.status,
    query.createdAfter,
    query.createdBefore,
    query.sortOrder,
    query.search,
    debouncedCompanyId,
  ]);

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      const res = await fetchList(queryParams);
      if (cancelled) return;
      setListData(res || null);
    })();
    return () => {
      cancelled = true;
    };
  }, [queryParams, fetchList]);

  const handleQueryChange = (key, value) => {
    if (key === 'reset') {
      setQuery({});
      setCurrentPage(0);
    } else {
      setQuery((prev) => {
        const next = { ...prev, [key]: value };
        if (!value) delete next[key];
        return next;
      });
    }
  };

  const handlePage = (pageNum) => {
    setCurrentPage(pageNum);
  };

  const padHorizontal = small ? '1rem' : '2rem';

  return (
    <AdminNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'Manual Claims Requests',
        subTitle: 'Content ID · QA',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad={{ vertical: '1.5rem' }}
        height="100%"
        gap="1rem"
        width="100%"
      >
        <InfoNotice
          header="QA queue"
          text="Review and update manual claim requests submitted by customers. Claim actions happen in YouTube Studio; use status, filters, and notes here to coordinate with the customer view."
        />

        <Box pad={{ horizontal: padHorizontal }} width="100%">
          <AdminManualClaimsRequestsTablePartial
            small={small}
            loading={loading}
            data={listData}
            handlePage={handlePage}
            currentPage={currentPage}
            query={query}
            onQueryChange={handleQueryChange}
            companyIdInput={companyIdInput}
            onCompanyIdChange={setCompanyIdInput}
            companyOptions={companyOptions}
            companyNameById={companyNameById}
            onCompanySelect={(value) => {
              setCompanyIdInput(value ? String(value) : '');
            }}
            onOpenDetail={(manualClaimId) => navigate(
              paths.adminManualClaimRequest.replace(':id', manualClaimId),
            )}
          />
        </Box>
      </Box>
    </AdminNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    loading: state.fetchLoader.dataLoading,
    companyList: state.adminCompany.companies,
  };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      fetchList: initiateAdminFetchManualClaimRequests,
      fetchCompanyList: fetchCompanyListRequest,
    },
    dispatch,
  );
}

AdminManualClaimsListPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  fetchList: PropTypes.func.isRequired,
  fetchCompanyList: PropTypes.func.isRequired,
  companyList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    name: PropTypes.string.isRequired,
  })),
  loading: PropTypes.bool.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(AdminManualClaimsListPage);
